import { ref, computed } from 'vue';
import { generateUUID } from '@/Core.Patterns/Factory';
import { useUndoRedo } from '@/Core.Patterns/Composables/useUndoRedo';
import * as API from '../API';
import { BLANK_BASE_LAYOUT, blankBaseLayoutFile } from '../blankBaseLayout';
import type {
  IDigitalTwin,
  IOverlayItem,
  ISegment,
  IEquipment,
  IEquipmentReading,
  IEquipmentSystemGroup,
} from '../types';

// ---------------------------------------------------------------------------
// Module-level singleton state (shared across component instances)
// ---------------------------------------------------------------------------
const siteId = ref<number | null>(null);
const digitalTwins = ref<IDigitalTwin[]>([]);
const currentDigitalTwinId = ref<number | null>(null);
/** Grouped equipment list for the palette — populated by load(). */
const equipmentGroups = ref<IEquipmentSystemGroup[]>([]);
/**
 * Latest readings keyed by equipmentId — populated by loadAllReadings() / fetchReadingsForItem().
 * An equipment item may have multiple readings.
 */
const equipmentReadings = ref<Record<number, IEquipmentReading[]>>({});
const isLoading = ref(false);
/** UUIDs of items highlighted with the blue multi-select ring */
const selectedOverlayItemIds = ref<string[]>([]);

// ---------------------------------------------------------------------------
// Saved-state baseline — mirrors the last successfully persisted state.
// Only updated on explicit saveAction(); used to compute isDirty and reset.
// ---------------------------------------------------------------------------
const savedBaselineMap = ref<
  Record<number, { overlay: IOverlayItem[]; segments: ISegment[] }>
>({});

// ---------------------------------------------------------------------------
// Undo / Redo — one instance per digital twin (floor)
// ---------------------------------------------------------------------------
type DTSnapshot = {
  twinId: number;
  overlay: IOverlayItem[];
  segments: ISegment[];
};
const undoRedoMap = new Map<number, ReturnType<typeof useUndoRedo>>();

function getUndoRedo(twinId: number): ReturnType<typeof useUndoRedo> {
  if (!undoRedoMap.has(twinId)) {
    undoRedoMap.set(twinId, useUndoRedo(30));
  }
  return undoRedoMap.get(twinId)!;
}

function snapshotState(): DTSnapshot | null {
  const dt = digitalTwins.value.find(
    (d) => d.id === currentDigitalTwinId.value
  );
  if (!dt) return null;
  return { twinId: dt.id, overlay: dt.overlay, segments: dt.segments };
}

function applyState(state: unknown): void {
  const s = state as DTSnapshot;
  const dt = digitalTwins.value.find((d) => d.id === s.twinId);
  if (!dt) return;
  dt.overlay = s.overlay;
  dt.segments = s.segments;
  selectedOverlayItemIds.value = [];
}

function commitSnapshot(): void {
  const snap = snapshotState();
  if (snap) getUndoRedo(snap.twinId).commit(snap);
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------
/** Tab id for a not-yet-created (placeholder) site-system tab. Negated so it can
 *  never collide with a real digital twin's (positive) id. */
const placeholderTabId = (siteSystemId: number): number => -siteSystemId;

export function useDigitalTwinMVP() {
  /**
   * Tabs rendered by the selector — one per site system, always.
   *
   * Each site system returned by the equipment endpoint
   * (GET /v1/digitaltwins/equipment?siteId=…) shows as a tab: its real digital
   * twin when one exists, otherwise a placeholder the user can set up. Any real
   * twin whose site system the equipment endpoint omitted is still appended, so
   * an existing twin is never hidden.
   *
   * Placeholder tabs use a negated siteSystemId as their tab id and a blank SVG
   * so equipment can be dropped before a floor plan is uploaded. The first save
   * creates the twin.
   */
  const siteSystemTabs = computed<IDigitalTwin[]>(() => {
    const realBySystem = new Map<number, IDigitalTwin>(
      digitalTwins.value.map((dt) => [dt.siteSystemId, dt])
    );

    const tabs: IDigitalTwin[] = equipmentGroups.value.map((g, i) => {
      const real = realBySystem.get(g.siteSystemId);
      return (
        real ?? {
          id: placeholderTabId(g.siteSystemId),
          siteSystemId: g.siteSystemId,
          baseLayout: BLANK_BASE_LAYOUT,
          overlay: [],
          segments: [],
          displayOrder: i + 1,
        }
      );
    });

    for (const dt of digitalTwins.value) {
      if (
        !equipmentGroups.value.some((g) => g.siteSystemId === dt.siteSystemId)
      ) {
        tabs.push(dt);
      }
    }

    return tabs;
  });

  const currentDigitalTwin = computed(
    () =>
      siteSystemTabs.value.find((dt) => dt.id === currentDigitalTwinId.value) ??
      null
  );

  const currentOverlay = computed<IOverlayItem[]>(
    () => currentDigitalTwin.value?.overlay ?? []
  );

  const currentSegments = computed<ISegment[]>(
    () => currentDigitalTwin.value?.segments ?? []
  );

  const onSegmentsChange = (segs: ISegment[]): void => {
    const dt = currentDigitalTwin.value;
    if (!dt) return;
    dt.segments = segs;
  };

  /** Load all data needed by the MVP for a given site. */
  const load = async (id: number): Promise<void> => {
    isLoading.value = true;
    const sameSite = id === siteId.value;
    // Remember the active site system so a placeholder → real-DT transition
    // (which changes the tab id but not the site system) keeps the user on the
    // same tab after a reload.
    const previousSiteSystemId = sameSite
      ? (currentDigitalTwin.value?.siteSystemId ?? null)
      : null;
    if (!sameSite) {
      currentDigitalTwinId.value = null;
      equipmentReadings.value = {};
      undoRedoMap.clear();
    }
    siteId.value = id;
    try {
      const [dts, groups] = await Promise.all([
        API.getDigitalTwins(id),
        API.getEquipmentGroups(id).catch(() => [] as IEquipmentSystemGroup[]),
      ]);
      digitalTwins.value = dts;
      equipmentGroups.value = groups;

      // Seed the saved baseline from the data returned by the API.
      const baselines: typeof savedBaselineMap.value = {};
      dts.forEach((dt) => {
        baselines[dt.id] = {
          overlay: JSON.parse(JSON.stringify(dt.overlay)),
          segments: JSON.parse(JSON.stringify(dt.segments)),
        };
      });
      // Placeholders start from an empty canvas. Seeding that baseline lets a
      // drop mark the tab dirty so the user can save the new twin.
      for (const tab of siteSystemTabs.value) {
        if (baselines[tab.id]) continue;
        baselines[tab.id] = { overlay: [], segments: [] };
      }
      savedBaselineMap.value = baselines;

      // Choose the tab to show. Priority: same site system as before (handles
      // the placeholder → real-DT swap after a background upload), then the
      // current tab if still valid, then the first tab by display order (matches
      // the selector's own ordering so we open on the visually-first tab).
      const tabs = siteSystemTabs.value;
      const firstByDisplayOrder = [...tabs].sort(
        (a, b) => a.displayOrder - b.displayOrder
      )[0];
      const preferred =
        (previousSiteSystemId !== null
          ? tabs.find((t) => t.siteSystemId === previousSiteSystemId)
          : undefined) ??
        tabs.find((t) => t.id === currentDigitalTwinId.value) ??
        firstByDisplayOrder;
      currentDigitalTwinId.value = preferred?.id ?? null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch readings for an equipment item from the API.
   * The response may include readings for PEER equipment (e.g. a Hot Water Valve
   * querying its Washing Machines). ALL returned readings are merged into the
   * equipmentReadings map so the canvas chips stay current for every affected item.
   *
   * Returns the raw flat array so the caller can group it into peer cards.
   *
   * ⚠️  MOCK — replace once the real endpoint is ready:
   *   GET /digital-twins/latest-readings?siteId={id}&equipmentIds={equipmentId}
   */
  const fetchReadingsForItem = async (
    equipmentId: number
  ): Promise<IEquipmentReading[]> => {
    const fetched = await API.getLatestReadings([equipmentId]).catch(
      () => [] as IEquipmentReading[]
    );
    // Group by equipmentId and merge ALL (primary + peers) into the map.
    const newEntries: Record<number, IEquipmentReading[]> = {};
    for (const r of fetched) {
      if (!newEntries[r.equipmentId]) newEntries[r.equipmentId] = [];
      newEntries[r.equipmentId].push(r);
    }
    equipmentReadings.value = { ...equipmentReadings.value, ...newEntries };
    return fetched;
  };

  /**
   * Batch-fetch readings for every equipment item currently placed on any
   * digital twin and merge them into equipmentReadings.
   * Called once after load() so the view-mode cards are populated immediately.
   */
  const loadAllReadings = async (): Promise<void> => {
    if (siteId.value === null) return;
    const allIds = [
      ...new Set(
        digitalTwins.value
          .flatMap((dt) => dt.overlay.map((o) => o.itemId))
          .filter((id): id is number => id != null)
      ),
    ];
    if (allIds.length === 0) return;
    const fetched = await API.getLatestReadings(allIds).catch(
      () => [] as IEquipmentReading[]
    );
    const map: Record<number, IEquipmentReading[]> = {};
    for (const r of fetched) {
      if (!map[r.equipmentId]) map[r.equipmentId] = [];
      map[r.equipmentId].push(r);
    }
    equipmentReadings.value = { ...equipmentReadings.value, ...map };
  };

  const selectDigitalTwin = (id: number): void => {
    currentDigitalTwinId.value = id;
    selectedOverlayItemIds.value = [];
  };

  /**
   * Place a new overlay item on the current digital twin's map.
   * @param equip          – the equipment item being placed
   * @param x              – horizontal position as a percentage (0–100) of canvas width
   * @param y              – vertical position as a percentage (0–100) of canvas height
   * @param siteSystemId   – id of the site system group the equipment belongs to
   * @param siteSystemName – display name of the site system group
   */
  const addOverlayItem = (
    equip: IEquipment,
    x: number,
    y: number,
    siteSystemId?: number,
    siteSystemName?: string
  ): void => {
    const dt = currentDigitalTwin.value;
    if (!dt) return;
    commitSnapshot();

    const item: IOverlayItem = {
      id: generateUUID(),
      // equipmentType drives SVG icon lookup — populated from the API's equipmentTypeName
      equipmentType: equip.equipmentTypeName,
      itemId: equip.equipmentId,
      name: equip.equipmentName,
      siteSystemId,
      siteSystemName,
      x,
      y,
    };

    dt.overlay.push(item);
  };

  /** Remove a placed overlay item by its UUID. */
  const removeOverlayItem = (itemId: string): void => {
    const dt = currentDigitalTwin.value;
    if (!dt) return;
    commitSnapshot();
    dt.overlay = dt.overlay.filter((i) => i.id !== itemId);
    selectedOverlayItemIds.value = selectedOverlayItemIds.value.filter(
      (id) => id !== itemId
    );
  };

  /** Select or toggle an overlay item. Pass multi=true for Ctrl/Cmd+click. */
  const selectItem = (id: string | null, multi: boolean): void => {
    if (id === null) {
      selectedOverlayItemIds.value = [];
    } else if (multi) {
      const idx = selectedOverlayItemIds.value.indexOf(id);
      if (idx >= 0) {
        selectedOverlayItemIds.value = selectedOverlayItemIds.value.filter(
          (x) => x !== id
        );
      } else {
        selectedOverlayItemIds.value = [...selectedOverlayItemIds.value, id];
      }
    } else {
      selectedOverlayItemIds.value = [id];
    }
  };

  /** Replace the entire selection with the given set of IDs (used by rubber-band selection). */
  const selectItems = (ids: string[]): void => {
    selectedOverlayItemIds.value = [...ids];
  };

  /** Remove every currently-selected overlay item in a single undo snapshot. */
  const removeSelectedItems = (): void => {
    const dt = currentDigitalTwin.value;
    if (!dt || selectedOverlayItemIds.value.length === 0) return;
    commitSnapshot();
    const toRemove = new Set(selectedOverlayItemIds.value);
    dt.overlay = dt.overlay.filter((i) => !toRemove.has(i.id));
    selectedOverlayItemIds.value = [];
  };

  /** Update the position of an existing overlay item (no snapshot — caller must call commitSnapshot before the drag starts). */
  const moveOverlayItem = (itemId: string, x: number, y: number): void => {
    const dt = currentDigitalTwin.value;
    if (!dt) return;
    const item = dt.overlay.find((i) => i.id === itemId);
    if (item) {
      item.x = x;
      item.y = y;
    }
  };

  /** Update the scale of an existing overlay item (no snapshot — caller must call commitSnapshot before the drag starts). */
  const resizeOverlayItem = (itemId: string, scale: number): void => {
    const dt = currentDigitalTwin.value;
    if (!dt) return;
    const item = dt.overlay.find((i) => i.id === itemId);
    if (item) item.scale = scale;
  };

  const canUndo = computed(() =>
    currentDigitalTwinId.value !== null
      ? getUndoRedo(currentDigitalTwinId.value).canUndo()
      : false
  );
  const canRedo = computed(() =>
    currentDigitalTwinId.value !== null
      ? getUndoRedo(currentDigitalTwinId.value).canRedo()
      : false
  );

  const undoAction = (): void => {
    if (currentDigitalTwinId.value === null) return;
    const current = snapshotState();
    getUndoRedo(currentDigitalTwinId.value).undo(current, applyState);
  };

  const redoAction = (): void => {
    if (currentDigitalTwinId.value === null) return;
    const current = snapshotState();
    getUndoRedo(currentDigitalTwinId.value).redo(current, applyState);
  };

  // ---------------------------------------------------------------------------
  // Dirty tracking / Save / Reset
  // ---------------------------------------------------------------------------

  /**
   * True when the active twin's in-memory state differs from the last saved state.
   * Drives the enabled state of Save and Reset.
   */
  const isDirty = computed((): boolean => {
    const twinId = currentDigitalTwinId.value;
    if (twinId === null) return false;
    const baseline = savedBaselineMap.value[twinId];
    if (!baseline) return false;
    return (
      JSON.stringify(currentOverlay.value) !==
        JSON.stringify(baseline.overlay) ||
      JSON.stringify(currentSegments.value) !==
        JSON.stringify(baseline.segments)
    );
  });

  const isSaving = ref(false);

  /** Persist the active twin's current overlay via PUT and mark clean. */
  const saveAction = async (): Promise<void> => {
    const dt = currentDigitalTwin.value;
    if (!dt) return;
    isSaving.value = true;
    const wasPlaceholder = dt.id < 0;
    try {
      const updated = await API.updateDigitalTwin(dt.siteSystemId, {
        // A placeholder has no stored layout yet. Send the blank SVG so the
        // created twin keeps a canvas that can be dropped onto.
        baseLayout: wasPlaceholder ? blankBaseLayoutFile() : undefined,
        overlay: JSON.stringify({ items: dt.overlay, segments: dt.segments }),
        displayOrder: dt.displayOrder,
      });
      if (wasPlaceholder) {
        digitalTwins.value = [...digitalTwins.value, updated];
        currentDigitalTwinId.value = updated.id;
      } else {
        // Reflect the server-confirmed state as the new baseline
        dt.overlay = updated.overlay;
        dt.segments = updated.segments;
      }
      savedBaselineMap.value = {
        ...savedBaselineMap.value,
        [updated.id]: {
          overlay: JSON.parse(JSON.stringify(updated.overlay)),
          segments: JSON.parse(JSON.stringify(updated.segments)),
        },
      };
      // Clear undo/redo so history doesn't cross save boundaries
      getUndoRedo(dt.id).clear();
      if (wasPlaceholder) getUndoRedo(updated.id).clear();
    } finally {
      isSaving.value = false;
    }
  };

  /** Revert the active twin to the last saved state. */
  const resetAction = (): void => {
    const dt = currentDigitalTwin.value;
    if (!dt) return;
    const baseline = savedBaselineMap.value[dt.id];
    if (!baseline) return;
    dt.overlay = JSON.parse(JSON.stringify(baseline.overlay));
    dt.segments = JSON.parse(JSON.stringify(baseline.segments));
    selectedOverlayItemIds.value = [];
    getUndoRedo(dt.id).clear();
  };

  return {
    digitalTwins,
    siteSystemTabs,
    currentDigitalTwinId,
    currentDigitalTwin,
    currentOverlay,
    currentSegments,
    onSegmentsChange,
    equipmentGroups,
    equipmentReadings,
    isLoading,
    selectedOverlayItemIds,
    load,
    loadAllReadings,
    fetchReadingsForItem,
    selectDigitalTwin,
    selectItem,
    selectItems,
    addOverlayItem,
    removeOverlayItem,
    removeSelectedItems,
    moveOverlayItem,
    resizeOverlayItem,
    commitSnapshot,
    canUndo,
    canRedo,
    undoAction,
    redoAction,
    isDirty,
    isSaving,
    saveAction,
    resetAction,
  };
}
